import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Spinner } from '@/components/ui/Spinner';
import { apiFetch } from '@/api/client';
import { cn } from '@/lib/cn';

/**
 * PERSONAS — ordenadas por quién necesita atención, no alfabéticamente.
 *
 * Mandato: *"Lista: personas ordenadas por «necesita atención». Un semáforo
 * marca la temperatura y va con el motivo."*
 *
 * ═══ EL MOTIVO VA SIEMPRE AL LADO DEL COLOR ═══
 *
 * Un punto rojo sin explicación obliga a abrir la ficha para saber qué pasa, y
 * eso es justo el trabajo que la lista venía a ahorrar. "20 días sin escribir"
 * se entiende sin tocar nada.
 */

interface Persona {
  id: string;
  nombre: string;
  telefono: string | null;
  conversationId: string | null;
  diasSinHablar: number | null;
  pendientes: number;
  temperatura: 'ROJO' | 'AMARILLO' | 'VERDE';
  motivo: string;
}

const PUNTO: Record<Persona['temperatura'], string> = {
  ROJO: 'bg-red-500',
  AMARILLO: 'bg-amber-500',
  VERDE: 'bg-emerald-500',
};

export default function Personas() {
  const navegar = useNavigate();
  const datos = useQuery({
    queryKey: ['personas'],
    queryFn: () => apiFetch<{ personas: Persona[] }>('/api/personas'),
  });

  const lista = datos.data?.personas ?? [];

  return (
    <div className="space-y-3">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Personas</h1>
        <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-300">
          Primero quien necesita tu atención.
        </p>
      </div>

      <Card>
        <CardBody className="space-y-0">
          {datos.isPending ? (
            <Spinner />
          ) : datos.isError ? (
            <p className="py-6 text-center text-sm text-red-600 dark:text-red-300">
              No se pudieron leer tus personas.
            </p>
          ) : lista.length === 0 ? (
            <p className="py-10 text-center text-sm text-slate-400">
              Todavía no hay personas. Aparecen cuando Athena lee tus conversaciones.
            </p>
          ) : (
            lista.map((p) => (
              <button
                key={p.id}
                onClick={() => navegar(`/personas/${p.id}`)}
                className="flex min-h-[60px] w-full items-center gap-3 border-b border-slate-100 py-3 text-left last:border-0 dark:border-slate-800"
              >
                <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', PUNTO[p.temperatura])} />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-900 dark:text-slate-100">
                    {p.nombre}
                  </span>
                  {/* El motivo, siempre junto al color. */}
                  <span className="block text-xs text-slate-500 dark:text-slate-400">
                    {p.motivo}
                  </span>
                </span>
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-300" />
              </button>
            ))
          )}
        </CardBody>
      </Card>
    </div>
  );
}
